import { ICoordinateInfo } from "@/domain/entities/address";
import { ref } from "vue";

export const useNaverMap = (
  latitude = 37.2529797,
  longitude = 127.4806853,
  infoBorderColor: string,
  zoomLevel = 14,
  minZoomLevel = 8,
  maxZoomLevel = 16
) => {
  const map = ref<any>(null);

  const addScript = () => {
    const naverMapScript = document.createElement("script");
    naverMapScript.setAttribute(
      "src",
      `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.VUE_APP_NAVER_CLIENT_ID}&submodules=panorama,drawing,geocoder`
    );
    naverMapScript.async = true;
    document.head.appendChild(naverMapScript);
    naverMapScript.onload = () => addClusterScript();
  };

  /** 마커 클러스터 스크립트 추가 */
  const addClusterScript = () => {
    const clusterScript = document.createElement("script");
    clusterScript.type = "text/javascript";
    clusterScript.src =
      location.hostname === "localhost"
        ? "/js/MarkerClustering.js"
        : `${process.env.VUE_APP_PUBLIC_PATH}/js/MarkerClustering.js`;
    document.head.appendChild(clusterScript);
    clusterScript.onload = () => initMap();
  };

  const initMap = () => {
    setTimeout(() => {
      map.value = new window.naver.maps.Map("map", {
        center: getNaverMapPoint(latitude, longitude),
        zoom: zoomLevel,
        mapTypes: new window.naver.maps.MapTypeRegistry({
          normal: new window.naver.maps.NaverStyleMapTypeOptions.getNormalMap({
            overlayType: "bg.ol.ts.lko",
          }),
        }),
        minZoom: minZoomLevel,
        maxZoom: maxZoomLevel,
      });
    }, 300);
  };

  /** 지도 중심 위치 반환 */
  const getCenter = (): ICoordinateInfo => {
    if (!map.value) return { latitude: -1, longitude: -1 };

    const center = map.value.getCenter();
    return { latitude: center.y, longitude: center.x };
  };

  /** 지도 중심 위치 변경 */
  const setCenter = (lat?: number, lng?: number) => {
    if (!map.value || !lat || !lng) return;

    const center = getNaverMapPoint(lat, lng);
    map.value.panTo(center, { duration: 250, easing: "easeOutCubic" });
  };

  /** 네이버 지도의 위치 객체를 반환 */
  const getNaverMapPoint = (lat: number, lng: number) => {
    return new window.naver.maps.LatLng(lat, lng);
  };

  /** 마커 생성 */
  const createMarker = (
    lat: number,
    lng: number,
    markerImage: () => any,
    zIndex: number
  ) => {
    const position = getNaverMapPoint(lat, lng);
    const markerOptions = {
      position: position,
      map: map,
      icon: markerImage(),
      zIndex: zIndex,
    };
    return new window.naver.maps.Marker(markerOptions);
  };

  const updateMarkerStyle = (
    marker: any,
    markerImage: () => any,
    zIndex: number
  ) => {
    marker.setIcon(markerImage());
    marker.setZIndex(zIndex);
  };

  let infowindow: any = null;

  /** 정보창 생성 */
  const createInfoWindow = (marker: any, infoWindowContent: string) => {
    if (!infoWindowContent.length) return;

    infowindow = new window.naver.maps.InfoWindow({
      content: infoWindowContent,
      borderColor: infoBorderColor,
      borderWidth: 1,
      anchorSize: new window.naver.maps.Size(8, 8),
    });

    infowindow.open(map.value, marker);
  };

  /** 정보창 삭제 */
  const removeInfoWindow = () => {
    infowindow?.close();
  };

  return {
    map,
    addScript,
    initMap,
    getCenter,
    setCenter,
    getNaverMapPoint,
    createMarker,
    updateMarkerStyle,
    createInfoWindow,
    removeInfoWindow,
  };
};
